import React, { useState, useEffect, useContext, Suspense } from 'react';
import { Grid, Box, Skeleton, Typography } from '@mui/material';
import {
  DragDropContext,
  Droppable,
  Draggable
} from 'react-beautiful-dnd';

import { PermissionsContext } from '../../utils/PermissionsContext';
import { ModulesContext, isModuleActive } from '../../utils/ModulesContext';

const ViolationsChart = React.lazy(() => import('./ViolationsChart'));
const MaintenanceChart = React.lazy(() => import('./MaintenanceChart'));
const ArchitecturalRequestsChart = React.lazy(() => import('./ArchitecturalRequestsChart'));

const chartConfigMap = {
  violations: {
    id: 'violations',
    component: ViolationsChart,
    propName: 'violationsByMonth',
    permission: 'view_violations',
    moduleId: 2,
  },
  maintenance: {
    id: 'maintenance',
    component: MaintenanceChart,
    propName: 'maintenanceByMonth',
    permission: 'view_work_orders',
    moduleId: 1,
  },
  architectural: {
    id: 'architectural',
    component: ArchitecturalRequestsChart,
    propName: 'architecturalByMonth',
    permission: 'view_architectural_requests',
    moduleId: 3,
  }
};

const ChartSection = ({
  columns = 3,
  visibleCharts = ['violations', 'maintenance', 'architectural'],
  ...props
}) => {
  const permissionsCtx = useContext(PermissionsContext);
  const { modules } = useContext(ModulesContext);

  const hasPermission =
    typeof permissionsCtx === 'function'
      ? permissionsCtx
      : (perm) => Array.isArray(permissionsCtx) && permissionsCtx.includes(perm);

  const permittedCharts = visibleCharts.filter((id) => {
    const cfg = chartConfigMap[id];
    if (!cfg) return false;
    return (
      (!cfg.permission || hasPermission(cfg.permission)) &&
      isModuleActive(modules, cfg.moduleId)
    );
  });

  const [chartOrder, setChartOrder] = useState(permittedCharts);

  useEffect(() => {
    const saved = localStorage.getItem('chartOrder');
    if (saved) {
      const parsed = JSON.parse(saved);
      const filtered = parsed.filter(id => permittedCharts.includes(id));
      setChartOrder(filtered.length ? filtered : permittedCharts);
    } else {
      setChartOrder(permittedCharts);
    }
  }, [visibleCharts, permissionsCtx]);

  useEffect(() => {
    localStorage.setItem('chartOrder', JSON.stringify(chartOrder));
  }, [chartOrder]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const updatedOrder = Array.from(chartOrder);
    const [moved] = updatedOrder.splice(result.source.index, 1);
    updatedOrder.splice(result.destination.index, 0, moved);
    setChartOrder(updatedOrder);
  };

  const md = Math.floor(12 / Math.min(columns, chartOrder.length || 1));

  if (chartOrder.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography>You do not have access to any charts.</Typography>
      </Box>
    );
  }

  return (
    <Suspense fallback={<Skeleton variant="rectangular" height={300} width="100%" />}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="chart-grid" direction="horizontal">
          {(provided) => (
            <Grid
              container
              spacing={3}
              sx={{ marginTop: 3, alignItems: 'stretch' }}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {chartOrder.map((chartId, index) => {
                const chart = chartConfigMap[chartId];
                if (!chart) return null;

                const { component: Component, propName } = chart;

                return (
                  <Draggable draggableId={chartId} index={index} key={chartId}>
                    {(provided) => (
                      <Grid item xs={12} md={md} sx={{ display: 'flex' }}>
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{ width: '100%', display: 'flex' }}
                        >
                          <Component data={props[propName]} />
                        </Box>
                      </Grid>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>
    </Suspense>
  );
};

export default ChartSection;